import { onlineManager, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useShallow } from 'zustand/react/shallow';
import { DataQueryKey } from '../../../../hooks/useDataQuery/dataQuery.types';
import { useModalState } from '../../../../hooks/useModalState';
import LangEnum from '../../../../lang/LangEnum';
import { getFindProductGridKey } from '../../../../services/modules/product-grid/product-grid.keys';
import { useFindProductGrid } from '../../../../services/modules/product-grid/product-grid.queries';
import useOrderBookStore from '../../../../stores/OrderBook/orderBook.store';
import useProductDetailsStore from '../../../../stores/ProductDetails/productDetails.store';
import { OrderFilterStateEnum } from '../../../../stores/ProductDetailsFilter/productDetailsFilter.types';
import useSharedEntitystore from '../../../../stores/SharedEntities/sharedEntities.store';
import { shouldDisableGridByProductColorGridFlag } from '../../../../utils/shouldDisableGridByProductColorGridFlag';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useAuthContext } from '../../../login/AuthContext';
import { isOfflineStatus } from '../../../offline/domain/offlineOrder/offlineOrder.types';
import { useOfflineOrdersStore } from '../../../offline/stores/offlineOrders/offlineOrders.store';
import { UsedInOfflineMode } from '../../../offline/types/UsedInOfflineMode';
import useOrderBook from '../../../order-book/hooks/useOrderBook';
import { StatusType } from '../../../orders/order.types';
import useCopyPasteGrid from '../../../orders/products/hooks/useCopyPasteGrid';
import {
  OrderItemListGridItemDTO,
  OrderProductGridItem,
  UpdateItemFormat,
} from '../../../orders/products/orderProducts.types';
import {
  calculateProductTotalParts,
  calculateTotalOrderProduct,
} from '../../../orders/products/orderProducts.utils';
import { OrderProductForGridContainerTransformer } from '../../../orders/products/product/grid/components/orderProductGrid/components/grid/types/orderProductGridContainer.types';
import { useProductGrid } from '../../../orders/products/product/grid/components/orderProductGrid/hooks/useProductGrid/useProductGrid';
import { SelectPacksSideEffect } from '../../../orders/products/product/grid/components/orderProductGrid/types/commonGrid.types';
import { useProductGridActions } from '../../../orders/products/product/grid/hooks/useProductGridActions';
import { useGridStoreApi } from '../../../orders/products/product/grid/store/GridStoreProvider';
import { buildCombinationKey } from '../../../orders/products/product/grid/store/gridStore.utils';
import { produceItemsToStore } from '../../../orders/products/product/grid/store/produceItemsToStore';
import { getTotalPackQuantity } from '../../../orders/products/product/grid/utils/packQuantity.utils';
import { useTranslation } from '../../../translation/TranslationContext';
import { AvailabilityEnum } from '../../ProductTypes';
import { useProductDetailsOrderData } from '../contexts/ProductDetailsOrderData.context';
import { ProductDetailsDTO } from '../types/productDetails.types';
import { buildOfflineGridItem, getButtonStatus, getFakeProductItem } from './useCombination.utils';
import useProductDetailsEmptyAvailability from './useProductDetailsEmptyAvailability';
import useProductDetailsActions from './useProductsDetailsActions';

interface CombinationProviderProps extends UsedInOfflineMode {
  combination: ProductDetailsDTO;
  isSuggested: boolean;
  disableGrid?: boolean;
  refetchUsingOrderIds: (orderIds: string[]) => Promise<void> | undefined;
}

export enum CombinationButtonStatusEnum {
  WITH_PIECES = 'WITH_PIECES',
  ADDED = 'ADDED',
  SET_WINDOW = 'SET_WINDOW',
  ADD = 'ADD',
  LOADING = 'LOADING',
}

export interface IGridStatus {
  type: CombinationButtonStatusEnum;
  itemQuantity: number;
}

const useCombination = ({
  disableGrid,
  combination,
  isSuggested,
  refetchUsingOrderIds,
  isOfflineMode = false,
}: CombinationProviderProps) => {
  const { translate } = useTranslation();
  const sharedEntity = useSharedEntitystore((state) => state.sharedEntity);
  const { loggedin } = useAuthContext();
  const queryClient = useQueryClient();
  const { team } = useApplicationContext();
  const isPublicAccess = !!sharedEntity?.id && !loggedin;
  const {
    isOpen: isLoginModalOpen,
    openModal: openLoginModal,
    closeModal: closeLoginModal,
  } = useModalState();
  const { openOrderBook, allOrders } = useOrderBook();
  const { handleCloseModal, handleRemoveOrderProduct, getProductDetailsSummaryKey } =
    useProductDetailsActions();
  const { handleRemoveOrderProductWithEmptyAvailability, hasEmptyAvailability } =
    useProductDetailsEmptyAvailability(combination);
  const { setOpenedByOrderBook, from, copiedGrid, ordersFilterState, markCombinationAsPasted } =
    useProductDetailsStore(
      useShallow((state) => ({
        setOpenedByOrderBook: state.setOpenedByOrderBook,
        from: state.from,
        copiedGrid: state.copiedGrid,
        ordersFilterState: state.ordersFilterState,
        markCombinationAsPasted: state.markCombinationAsPasted,
      })),
    );

  const [gridButtonStatus, setGridButtonStatus] = useState<IGridStatus>();

  const { selectedOrderId, selectedOrderStatus, reloadOrderItemsQuantity } = useOrderBookStore(
    useShallow((state) => ({
      selectedOrderId: state.selectedOrder?.id,
      selectedOrderStatus: state.selectedOrder?.status,
      reloadOrderItemsQuantity: state.reloadOrderItemsQuantity,
    })),
  );

  const availabilityType = combination.futureStockDeliveryWindowId
    ? AvailabilityEnum.FUTURE_STOCK
    : combination.availability;
  const deliveryWindowId = combination.futureStockDeliveryWindowId
    ? combination.futureStockDeliveryWindowId
    : combination.deliveryWindowId;

  const gridStoreApi = useGridStoreApi();
  const combinationKey = buildCombinationKey({
    productColorId: combination.productColorId,
    availability: availabilityType,
    deliveryWindowId,
    orderId: combination.orderId,
  });

  const {
    grid,
    suggestedGrid,
    suggestedPacks,
    showSuggestedGrid,
    suggestionAchieved,
    hasValidPacks,
    selectedPacks,
    resetSelectedPacks,
  } = useProductGrid({
    availability: availabilityType,
    productColorId: combination.productColorId,
    deliveryWindowId,
    orderId: combination.orderId,
    orderStatus: allOrders.find((order) => order.id === combination.orderId)?.status,
    useDefaultPriceTableWhenOffline: true,
    isOfflineMode,
  });

  const { getSizes } = useProductDetailsOrderData();
  const sizes = getSizes(combination?.orderId);

  const buildGrid = useCallback((): OrderProductGridItem[] => {
    if (!sizes || !Array.isArray(sizes)) {
      return [];
    }

    return sizes.map((size) => {
      const found = grid.find((sku) => sku.productSizeId === size.id);

      return found || getFakeProductItem(size);
    });
  }, [grid, sizes]);

  const formattedGrid = useMemo(() => buildGrid(), [buildGrid]);

  useEffect(() => {
    if (gridStoreApi) {
      gridStoreApi.getState().seed({ combinationKey, grid: formattedGrid });
    }
  }, [gridStoreApi, combinationKey, formattedGrid]);

  const refetchAllOrderProductGridOnUpdate = () => {
    allOrders.forEach((order) => {
      if (
        order.id === combination.orderId ||
        ordersFilterState !== OrderFilterStateEnum.ALL_ORDERS
      ) {
        return;
      }

      queryClient.refetchQueries({
        queryKey: [
          team?.id,
          ...getFindProductGridKey({
            productColorId: combination.productColorId,
            availability: availabilityType,
            deliveryWindowId: deliveryWindowId,
            orderId: order.id,
            isOfflineMode: isOfflineStatus(order.status),
          }),
        ],
      });
    });
  };

  const refetchCombinationsValidationOnUpdate = () => {
    if (isOfflineMode) {
      return;
    }

    queryClient.refetchQueries({
      queryKey: [DataQueryKey.PRODUCT_DETAILS_COMBINATIONS_VALIDATION],
    });
  };

  const product = useMemo(
    () => OrderProductForGridContainerTransformer.fromProductDetails(combination),
    [combination],
  );

  const productForGrid = useMemo(() => ({ ...product, combinationKey }), [product, combinationKey]);

  const { onClickPaste } = useCopyPasteGrid({
    copiedGrid,
    grid,
    orderId: combination.orderId,
    product: productForGrid,
    resetSelectedPacks,
    onPasted: markCombinationAsPasted,
    isOfflineMode,
  });

  const partsQuantity = calculateProductTotalParts(grid);
  const total = calculateTotalOrderProduct(grid);
  const productSizeNames = useMemo(() => grid.map((gridItem) => gridItem.productSizeName), [grid]);

  const actionWithNoSelectedOrder = useCallback(
    (productColorId: string) => {
      openOrderBook();
      handleCloseModal();
      setOpenedByOrderBook({ state: true, productColorId, productId: combination.productId, from });
    },
    [handleCloseModal, openOrderBook, setOpenedByOrderBook, combination.productId],
  );

  const ensureSelectedOrder = useCallback(
    (callback: () => void) => {
      if (!selectedOrderId) {
        return actionWithNoSelectedOrder(combination.productColorId);
      }

      callback();
    },
    [actionWithNoSelectedOrder, selectedOrderId, combination.productColorId],
  );

  const { refetch } = useFindProductGrid({
    deliveryWindowId: combination.futureStockDeliveryWindowId || combination.deliveryWindowId,
    availability: combination.availability,
    productColorId: combination.productColorId,
    orderId: combination.orderId,
    isOfflineMode: isOfflineMode || !onlineManager.isOnline(),
  });

  const updateOrderItems = useCallback(
    async (gridItems: OrderItemListGridItemDTO[] = []) => {
      if (!gridItems.length) {
        return;
      }

      refetchCombinationsValidationOnUpdate();
      refetch();
      if (allOrders.length > 1) {
        refetchAllOrderProductGridOnUpdate();
      }
      const combinationIsInOrderItems = gridItems.some((item) => {
        const productColorIdMatches = item.color.id === combination.productColorId;
        const availabilityMatches = item.availability === availabilityType;

        const isReadyForDelivery = item.availability === AvailabilityEnum.READY_FOR_DELIVERY;
        const deliveryWindowIdMatches = item.deliveryWindowId === deliveryWindowId;

        const readyForDeliveryValidation = isReadyForDelivery && availabilityMatches;
        const futureStockOrPreOrderValidation = availabilityMatches && deliveryWindowIdMatches;
        return (
          productColorIdMatches && (readyForDeliveryValidation || futureStockOrPreOrderValidation)
        );
      });

      if (hasEmptyAvailability && combinationIsInOrderItems) {
        await handleRemoveOrderProductWithEmptyAvailability();
      }
    },
    [
      refetch,
      hasEmptyAvailability,
      handleRemoveOrderProductWithEmptyAvailability,
      availabilityType,
      deliveryWindowId,
    ],
  );

  const addProductToOfflineOrder = useCallback(async () => {
    if (!combination.orderId) {
      return;
    }

    const params = buildOfflineGridItem({
      availability: combination.availability,
      skuId: grid[0].skuId,
      productColorId: combination.productColorId,
      deliveryWindowId,
    });

    if (!params) {
      return;
    }

    const offlineOrdersStore = useOfflineOrdersStore.getState();
    await offlineOrdersStore.addProductBasedOnGrid({
      orderId: combination.orderId,
      gridItems: [params],
    });

    const queryKey = getFindProductGridKey({
      deliveryWindowId,
      orderId: combination.orderId,
      availability: combination.availability,
      isOfflineMode: isOfflineMode || !onlineManager.isOnline(),
      productColorId: combination.productColorId,
    });

    queryClient.refetchQueries({
      type: 'active',
      queryKey: [team?.id, ...queryKey],
    });
  }, [grid, queryClient, deliveryWindowId, team?.id, combination, isOfflineMode]);

  const handleStackOrderItem = useCallback(
    (item: UpdateItemFormat) => {
      if (!selectedOrderId || !combination.orderId) {
        actionWithNoSelectedOrder(combination.productColorId);
        return;
      }

      const productAlreadyInOrder = grid.some((gridItem) => !!gridItem.orderItemId);

      const offlineItemId = isOfflineMode && !productAlreadyInOrder ? uuidv4() : undefined;
      const offlineProductColorId = isOfflineMode ? combination.productColorId : undefined;

      produceItemsToStore({
        api: gridStoreApi,
        combinationKey,
        items: [
          {
            ...item,
            orderId: combination.orderId,
            id: item.id ?? offlineItemId,
            productColorId: item.productColorId ?? offlineProductColorId,
          },
        ],
      });
    },
    [
      selectedOrderId,
      gridStoreApi,
      combinationKey,
      combination.orderId,
      combination.productColorId,
      actionWithNoSelectedOrder,
      grid,
      isOfflineMode,
    ],
  );

  const { handleSelectSuggestion, handleSelectPacks } = useProductGridActions({
    grid,
    availability: availabilityType,
    deliveryWindowId,
    orderId: combination.orderId,
    productColorId: combination.productColorId,
    block: {
      isBlocked: !selectedOrderId,
      onBlocked: () => actionWithNoSelectedOrder(combination.productColorId),
    },
  });

  const handleRemoveStackOrderProduct = useCallback(
    async (item: UpdateItemFormat) => {
      if (!selectedOrderId || !combination.orderId) {
        return;
      }
      setGridButtonStatus({ itemQuantity: 0, type: CombinationButtonStatusEnum.LOADING });

      await handleRemoveOrderProduct({ orderId: combination.orderId, isSuggested, item });
      gridStoreApi.getState().clearCombination(combinationKey);
      await refetchUsingOrderIds([combination.orderId]);

      setGridButtonStatus({ itemQuantity: 0, type: CombinationButtonStatusEnum.ADD });
      refetchCombinationsValidationOnUpdate();
    },
    [selectedOrderId, combination.orderId],
  );

  const reloadOrderBookItemsQuantity = useCallback(async () => {
    const isSelectedOrder = selectedOrderId === combination.orderId;
    await reloadOrderItemsQuantity(true, combination.orderId, isSelectedOrder);
  }, [selectedOrderId, combination.orderId, reloadOrderItemsQuantity]);

  const onSubmitOrderItemsWithoutGrid = useCallback(async () => {
    if (isOfflineMode) {
      await addProductToOfflineOrder();
      await reloadOrderBookItemsQuantity();
      return;
    }

    const item: UpdateItemFormat = {
      skuId: grid[0].skuId,
      deliveryWindowId:
        combination.availability === AvailabilityEnum.FUTURE_STOCK
          ? combination.futureStockDeliveryWindowId
          : combination.deliveryWindowId,
      quantity: 0,
      availability: combination.availability,
      productColorId: combination.productColorId,
    };

    handleStackOrderItem(item);
  }, [
    grid,
    isOfflineMode,
    combination.availability,
    combination.productColorId,
    combination.deliveryWindowId,
    combination.futureStockDeliveryWindowId,
    handleStackOrderItem,
    addProductToOfflineOrder,
    reloadOrderBookItemsQuantity,
  ]);

  const onAddOrderProduct = useCallback(() => {
    if (isPublicAccess) {
      return openLoginModal();
    }

    if (!selectedOrderId || !combination.orderId) {
      actionWithNoSelectedOrder(combination.productColorId);
      return;
    }

    onSubmitOrderItemsWithoutGrid();
  }, [
    isPublicAccess,
    selectedOrderId,
    combination.orderId,
    combination.productColorId,
    openLoginModal,
    actionWithNoSelectedOrder,
    onSubmitOrderItemsWithoutGrid,
  ]);

  const onRemoveOrderProduct = useCallback(() => {
    if (isPublicAccess) {
      return openLoginModal();
    }

    const item: UpdateItemFormat = {
      skuId: grid[0]?.skuId,
      deliveryWindowId:
        combination.availability === AvailabilityEnum.FUTURE_STOCK
          ? combination.futureStockDeliveryWindowId
          : combination.deliveryWindowId,
      quantity: 0,
      availability: combination.availability,
      productColorId: combination.productColorId,
    };

    handleRemoveStackOrderProduct(item);
  }, [isPublicAccess]);

  const selectPacksSideEffect: SelectPacksSideEffect = useCallback(
    (packs) => {
      const sum = packs.reduce(
        (acc, cur) => acc + getTotalPackQuantity(cur.quantitiesBySize, cur.multiplier),
        0,
      );
      setGridButtonStatus({
        itemQuantity: sum,
        type: sum > 0 ? CombinationButtonStatusEnum.WITH_PIECES : CombinationButtonStatusEnum.ADDED,
      });
      queryClient.refetchQueries({ queryKey: getProductDetailsSummaryKey(combination.orderId) });
      reloadOrderBookItemsQuantity();
    },
    [reloadOrderBookItemsQuantity],
  );

  const getAvailabilityLabel = useCallback(() => {
    const availabilityLabelMapper = {
      [AvailabilityEnum.READY_FOR_DELIVERY]: translate(LangEnum.GLOBAL_READY_FOR_DELIVERY),
      [AvailabilityEnum.FUTURE_STOCK]:
        combination.deliveryWindowName?.toLowerCase() ||
        translate(LangEnum.GLOBAL_TO_DEFINE_DELIVERY_SHORT),
      [AvailabilityEnum.INFINITE_PRE_ORDER]:
        combination.deliveryWindowName?.toLowerCase() ||
        translate(LangEnum.GLOBAL_TO_DEFINE_DELIVERY_SHORT),
      [AvailabilityEnum.MATERIAL_PRE_ORDER]:
        combination.deliveryWindowName?.toLowerCase() ||
        translate(LangEnum.GLOBAL_TO_DEFINE_DELIVERY_SHORT),
      [AvailabilityEnum.EMPTY]: translate(LangEnum.GLOBAL_TO_DEFINE_DELIVERY_SHORT),
    };

    return (
      availabilityLabelMapper[combination.availability] ||
      translate(LangEnum.GLOBAL_TO_DEFINE_DELIVERY_SHORT)
    );
  }, [combination.availability, combination.deliveryWindowName, translate]);

  useEffect(
    () => gridStoreApi.registerAfterSave(combinationKey, updateOrderItems),
    [gridStoreApi, combinationKey, updateOrderItems],
  );

  const formattedGridButtonStatus = useMemo(
    () => gridButtonStatus || getButtonStatus({ grid, hasEmptyAvailability }),
    [gridButtonStatus, grid, hasEmptyAvailability],
  );

  const combinationHasOrderItemFromKit = () => {
    return grid.some((gridItem) => !!gridItem.orderItemKitId);
  };
  const isCombinationAvailableForCustomer = combination.isAvailableForCustomer !== false;
  const isCombinationWithOrderItem = grid.some((gridItem) => !!gridItem.orderItemId);
  const shouldDisableGridBasedOnCustomerAvailability =
    !isCombinationAvailableForCustomer && !isCombinationWithOrderItem;

  const shouldDisableGrid =
    disableGrid ||
    shouldDisableGridBasedOnCustomerAvailability ||
    shouldDisableGridByProductColorGridFlag({
      availability: combination.availability,
      allowChangeImmediateGrid: combination.allowChangeImmediateGrid,
      allowChangePreOrderGrid: combination.allowChangePreOrderGrid,
    }) ||
    combinationHasOrderItemFromKit();
  const shouldDisableAddButton =
    disableGrid || shouldDisableGridBasedOnCustomerAvailability || combinationHasOrderItemFromKit();

  return {
    total,
    combinationKey,
    hasEmptyAvailability,
    hasValidPacks,
    partsQuantity,
    selectedPacks,
    suggestedGrid,
    suggestedPacks,
    isLoginModalOpen,
    productSizeNames,
    showSuggestedGrid,
    suggestionAchieved,
    grid: formattedGrid,
    gridButtonStatus: formattedGridButtonStatus,
    combinationDeliveryWindowId: deliveryWindowId,
    combinationOrderStatus: isSuggested ? StatusType.SUGGESTED : selectedOrderStatus,
    shouldDisableGrid,
    shouldDisableAddButton,
    shouldDisableGridBasedOnCustomerAvailability,
    closeLoginModal,
    handleSelectPacks,
    onAddOrderProduct,
    resetSelectedPacks,
    ensureSelectedOrder,
    getAvailabilityLabel,
    onRemoveOrderProduct,
    selectPacksSideEffect,
    handleSelectSuggestion,
    handleClickPaste: onClickPaste,
    stackOrderItem: handleStackOrderItem,
  };
};

export default useCombination;
